// ABOUTME: SPEC-82 §4.2 — the tap-to-talk hold on its own: the gate machine, arming (D-18), and the tap that releases the welcome.
// ABOUTME: Split out of useIntroExperiment (review M3) so each unit stays under the function-size gate; behaviour is unchanged.

import { useCallback, useRef, useState } from 'react';
import { markIntroSeen } from '../state/visitMemory';
import { GATE, GATE_EVENT, gateReducer } from './tapGate';

/** Why the tap gate did or did not engage — a log value, never guest data. */
function armReason({ toggle, reelMode, seenBefore, withMic, landed }) {
  if (!toggle) return 'toggle';
  if (landed) return 'landing'; // no_enter_gate: the guest already tapped the CTA to get here — never ask twice
  if (!reelMode) return 'not_reel';
  if (seenBefore) return 'returning';
  if (!withMic) return 'consent';
  return 'engaged';
}

/**
 * @param {object} p the useIntroExperiment inputs the gate needs (see there)
 * @returns {{ holding: boolean, isHolding: Function, arm: Function, tap: Function, markSeen: Function }}
 */
export default function useTapGate({ ux, reelMode, hasSeenIntroBefore, showIntroRef, sendIntroFinished, isSocketOpen, landed = false }) {
  const [gate, setGate] = useState(GATE.IDLE);
  // The ref is the truth for the funnel and for long-lived socket closures (they outlive renders);
  // the state only exists so the footer re-renders when the CTA should swap to the dots.
  const gateRef = useRef(GATE.IDLE);
  const sendRef = useRef(sendIntroFinished);
  sendRef.current = sendIntroFinished;
  const openRef = useRef(isSocketOpen);
  openRef.current = isSocketOpen;

  const move = useCallback((event) => {
    gateRef.current = gateReducer(gateRef.current, event);
    setGate(gateRef.current);
  }, []);
  const isHolding = useCallback(() => gateRef.current === GATE.HOLDING, []);

  // Arms iff it can work (D-18). Raises the film gate SYNCHRONOUSLY: the connect effect builds the
  // URL (intro_playing=1) before the ref-mirror effect runs (trap T-1).
  const arm = useCallback((withMic) => {
    const reason = armReason({ toggle: !!ux.tap_to_talk, reelMode, seenBefore: hasSeenIntroBefore, withMic, landed });
    if (ux.tap_to_talk) console.log(`[SPEC-82][TAP] armed=${reason === 'engaged'} reason=${reason}`);
    if (reason !== 'engaged') return false;
    move(GATE_EVENT.ARM);
    showIntroRef.current = true;
    return true;
  }, [ux.tap_to_talk, reelMode, hasSeenIntroBefore, showIntroRef, move, landed]);

  // The tap: drop the film gate, write the visit record (the owner's rule: at the moment the welcome
  // is released), then release the held welcome through the ordinary funnel.
  const tap = useCallback(() => {
    if (!isHolding()) return;
    // A tap before the socket opens is banked by the funnel and delivered by onopen.
    const sendBeforeOpen = !(openRef.current?.() ?? true);
    move(GATE_EVENT.RELEASE);
    showIntroRef.current = false;
    markIntroSeen();
    console.log(`[SPEC-82][TAP] released sendBeforeOpen=${sendBeforeOpen}`);
    sendRef.current?.();
  }, [isHolding, move, showIntroRef]);

  // Replaces markIntroSeen() at the film's end/skip. Under a held gate the record waits for the tap.
  const markSeen = useCallback(() => {
    if (isHolding()) return;
    markIntroSeen();
  }, [isHolding]);

  return { holding: gate === GATE.HOLDING, isHolding, arm, tap, markSeen };
}
